<template>
  <component
    :is="tag"
    class="brand-logo-mark"
    :class="[
      `brand-logo-mark--${size}`,
      `brand-logo-mark--${layout}`,
      {
        'brand-logo-mark--image-only': showImageLogo,
        'brand-logo-mark--light-name-surface': lightNameSurface,
      },
    ]"
  >
    <img
      v-if="showImageLogo"
      :src="imageSrc"
      :alt="name"
      class="brand-logo-mark__img"
    />
    <template v-else>
      <span
        class="brand-logo-mark__avatar"
        :style="avatarStyle"
        aria-hidden="true"
      >
        {{ initial }}
      </span>
      <span v-if="showName" class="brand-logo-mark__name" :style="nameStyle">{{ name }}</span>
    </template>
  </component>
</template>

<script>
import {
  DEFAULT_CLINCH_LOGO_URL,
  contrastTextOnBackground,
  getBrandNameInitial,
  hasCustomBrandLogo,
  isLightBackground,
  resolveThemePrimaryHex,
  shouldShowBrandLogoMark,
} from '@/theme/brandLogo'

export default {
  name: 'BrandLogoMark',
  props: {
    name: { type: String, default: '' },
    logoUrl: { type: String, default: '' },
    primaryColor: { type: String, default: '' },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (v) => ['horizontal', 'stacked', 'mark-only'].includes(v),
    },
    showName: { type: Boolean, default: true },
    tag: { type: String, default: 'div' },
    /** When true, always use the generated mark (preview / forced tenant UI). */
    forceMark: { type: Boolean, default: false },
    tenantHost: { type: Boolean, default: undefined },
    /** Name sits on a white/light card (footer pill, previews). */
    lightNameSurface: { type: Boolean, default: false },
  },
  computed: {
    showImageLogo() {
      if (this.forceMark) return false
      if (hasCustomBrandLogo(this.logoUrl)) return true
      return !shouldShowBrandLogoMark(this.logoUrl, {
        tenantHost: this.tenantHost,
      })
    },
    imageSrc() {
      const url = (this.logoUrl || '').trim()
      return url || DEFAULT_CLINCH_LOGO_URL
    },
    initial() {
      return getBrandNameInitial(this.name)
    },
    resolvedPrimaryHex() {
      const raw = (this.primaryColor || '').trim()
      if (raw.startsWith('#')) return raw
      return resolveThemePrimaryHex('#33B5FF')
    },
    avatarStyle() {
      const fallbackBg =
        this.primaryColor ||
        'var(--clinch-blue-primary, var(--btn-primary, #33B5FF))'
      const bg = `var(--brand-logo-avatar-bg, ${fallbackBg})`
      return {
        background: bg,
        color: contrastTextOnBackground(this.resolvedPrimaryHex),
      }
    },
    nameStyle() {
      if (this.lightNameSurface) {
        return { color: '#001F3F' }
      }
      if (typeof document === 'undefined') {
        return { color: 'var(--sidebar-fg, var(--navbar-fg, #001F3F))' }
      }
      const root = document.documentElement
      const fg =
        getComputedStyle(root).getPropertyValue('--sidebar-fg').trim() ||
        getComputedStyle(root).getPropertyValue('--navbar-fg').trim()
      if (fg && !isLightBackground(fg)) {
        return { color: fg }
      }
      return { color: 'var(--sidebar-fg, var(--navbar-fg, #001F3F))' }
    },
  },
}
</script>

<style scoped>
.brand-logo-mark {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.brand-logo-mark--horizontal {
  flex-direction: row;
  gap: 0.65rem;
}

.brand-logo-mark--stacked {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.brand-logo-mark--mark-only {
  gap: 0;
}

.brand-logo-mark__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 12px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12);
}

.brand-logo-mark--sm .brand-logo-mark__avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 10px;
  font-size: 0.95rem;
}

.brand-logo-mark--md .brand-logo-mark__avatar {
  width: 2.5rem;
  height: 2.5rem;
  font-size: 1.1rem;
}

.brand-logo-mark--lg .brand-logo-mark__avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 14px;
  font-size: 1.25rem;
}

.brand-logo-mark__name {
  font-weight: 800;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-logo-mark--light-name-surface .brand-logo-mark__name {
  color: var(--clinch-navy, #001f3f);
}

.brand-logo-mark--sm .brand-logo-mark__name {
  font-size: 0.8125rem;
}

.brand-logo-mark--md .brand-logo-mark__name {
  font-size: 0.9375rem;
}

.brand-logo-mark--lg .brand-logo-mark__name {
  font-size: 1.05rem;
}

.brand-logo-mark__img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  object-fit: contain;
  flex-shrink: 0;
}

.brand-logo-mark--sm .brand-logo-mark__img {
  max-height: 2rem;
}

.brand-logo-mark--md .brand-logo-mark__img {
  max-height: 2.75rem;
}

.brand-logo-mark--lg .brand-logo-mark__img {
  max-height: 3.25rem;
}
</style>
